import React, { useState, useEffect, useRef } from "react";
import api from "../services/api";
import ApoieButton from "../components/ApoieButton/ApoieButton";
import Editable from "../components/Editable";
import PageHero from "../components/PageHero/PageHero";
import heroFallbackUrl from "../assets/images/metodologia-hero-shaped.png";
import FormActions from "../components/Admin/FormActions/FormActions";
import { useAdminPage } from "../hooks/useAdminPage";
import { UploadImageIcon } from "../assets/UploadImageIcon";
import "./ParaEmpresas.css";

const VIDEO_UNAVAILABLE_MESSAGE = "O vídeo não está disponível no momento";
const COUNTERPART_SLOTS = 6;

function extractYouTubeId(url) {
  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.replace("www.", "");

    if (host === "youtu.be") {
      return parsedUrl.pathname.split("/").filter(Boolean)[0] || null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsedUrl.pathname === "/watch") {
        return parsedUrl.searchParams.get("v");
      }

      const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
      const embedLikeIndex = ["embed", "shorts", "live"].includes(
        pathSegments[0],
      )
        ? 1
        : -1;

      if (embedLikeIndex > 0) {
        return pathSegments[embedLikeIndex] || null;
      }
    }
  } catch (error) {
    return null;
  }

  return null;
}

function buildInstagramEmbedUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
    const contentType = pathSegments[0];
    const mediaId = pathSegments[1];

    if (!["p", "reel", "tv"].includes(contentType) || !mediaId) {
      return null;
    }

    return `https://www.instagram.com/${contentType}/${mediaId}/embed`;
  } catch (error) {
    return null;
  }
}

function getInstitutionalVideoEmbed(videoData) {
  if (!videoData?.url) {
    return null;
  }

  const platform = videoData.platform?.toLowerCase();

  if (platform === "youtube" || /youtu\.?be/.test(videoData.url)) {
    const videoId = extractYouTubeId(videoData.url);

    if (!videoId) {
      return null;
    }

    return {
      src: `https://www.youtube.com/embed/${videoId}`,
      title: "Video sobre a doação",
    };
  }

  if (platform === "instagram" || /instagram\.com/.test(videoData.url)) {
    const embedUrl = buildInstagramEmbedUrl(videoData.url);

    if (!embedUrl) {
      return null;
    }

    return {
      src: embedUrl,
      title: "Video sobre a doação no Instagram",
    };
  }

  return null;
}

const ParaEmpresas = () => {
  const [videoEmbed, setVideoEmbed] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(1);
  const [page, setPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const adminPage = useAdminPage();
  const fileInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadInstitutionalVideo() {
      try {
        const response = await api.get("/config/para-empresas");
        const embedData = getInstitutionalVideoEmbed(response.data.examples.video);

        if (!isMounted) {
          return;
        }

        setPage(response.data);
        setVideoEmbed(embedData);
      } catch (error) {
        if (isMounted) {
          setPage(null);
          setVideoEmbed(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadInstitutionalVideo();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const total = page?.introduction?.slideshow?.length || 0;
    if (total === 0) {
      setCurrent(0);
      return;
    }

    setCurrent((prev) => Math.min(prev, total - 1));
  }, [page?.introduction?.slideshow?.length]);

  const anterior = () => {
    const total = page?.introduction.slideshow.length || 0;
    if (total < 2) return;
    setCurrent((prev) => (prev === 0 ? total - 1 : prev - 1));
  };

  const proximo = () => {
    const total = page?.introduction.slideshow.length || 0;
    if (total < 2) return;
    setCurrent((prev) => (prev === total - 1 ? 0 : prev + 1));
  };

  const handleEditClick = () => {
    if (editMode) {
      setPage(prevPage);
      setError(null);
    } else setPrevPage(structuredClone(page));
    setEditMode((prev) => !prev);
  };

  const handleSave = () => {
    async function savePage() {
      try {
        const requiredValues = [
          page?.counterparts?.title,
          page?.examples?.title,
          page?.incomeTax?.title,
          page?.incomeTax?.meetingLink,
        ];

        if (requiredValues.some((value) => !String(value || "").trim())) {
          setError({ error: "Preencha todos os campos obrigatórios antes de salvar." });
          return;
        }

        for (let i = 0; i < page.introduction.slideshow.length; i++) {
          let slide = page.introduction.slideshow[i];

          // Se tiver a propriedade newFile, precisamos fazer o upload
          if (slide.newFile) {
            const formData = new FormData();
            formData.append("image", slide.newFile);

            const previousPublicId = String(
              slide.publicId || slide.public_id || "",
            ).trim();
            const uploadResponse = await api.post("/media/upload", formData);

            if (previousPublicId) {
              try {
                await api.delete("/media/remove", {
                  data: { public_id: previousPublicId },
                });
              } catch {
                // A imagem anterior pode já ter sido removida do Cloudinary.
                // Essa limpeza não deve impedir a substituição pela nova imagem.
              }
            }

            slide.url = uploadResponse.data.url;
            slide.publicId = uploadResponse.data.public_id;

            delete slide.newFile;
          }
        }
        const payload = {
          ...page,
          introduction: {
            ...page.introduction,
            title: String(page.introduction.title || "").trim(),
            text: page.introduction.text.trim(),
            slideshow: page.introduction.slideshow
              .filter((slide) => String(slide.url || "").trim())
              .map((slide, index) => ({
                ...slide,
                url: String(slide.url || "").trim(),
                publicId: String(slide.publicId || slide.public_id || "").trim(),
                order: index,
              })),
          },
          counterparts: {
            ...page.counterparts,
            title: page.counterparts.title.trim(),
            text: page.counterparts.text.trim(),
            items: Array.from(
              {
                length: Math.max(
                  COUNTERPART_SLOTS,
                  page.counterparts.items?.length || 0,
                ),
              },
              (_, index) => ({
                text: String(page.counterparts.items?.[index]?.text || "").trim(),
                order: index,
              }),
            ),
            image: {
              ...page.counterparts.image,
              url: String(page.counterparts.image?.url || "").trim(),
              publicId: String(page.counterparts.image?.publicId || page.counterparts.image?.public_id || "").trim(),
            },
          },
          examples: {
            ...page.examples,
            title: page.examples.title.trim(),
            video: {
              ...page.examples.video,
              url: String(page.examples.video?.url || "").trim(),
            },
          },
          incomeTax: {
            ...page.incomeTax,
            title: page.incomeTax.title.trim(),
            text: page.incomeTax.text.trim(),
            meetingLink: page.incomeTax.meetingLink.trim(),
          },
        };

        const response = await api.put("/config/para-empresas", payload);
        setPage(response.data);
        setVideoEmbed(getInstitutionalVideoEmbed(response.data.examples.video));
        setError(null);
        setEditMode(false);
      } catch (error) {
        setError(error);
      }
    }

    savePage();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Cria uma URL temporária para mostrar a imagem imediatamente na tela
    const imageUrl = URL.createObjectURL(file);

    const updatedSlideshow = [...(page?.introduction?.slideshow || [])];
    const targetIndex = updatedSlideshow[current] ? current : updatedSlideshow.length;

    // Substitui a URL do slide atual pela nova imagem e guarda o arquivo para o backend
    updatedSlideshow[targetIndex] = {
      ...updatedSlideshow[targetIndex],
      url: imageUrl,
      newFile: file // Essa propriedade pode ser usada no seu handleSave para enviar via FormData
    };

    setPage({
      ...page,
      introduction: {
        ...page.introduction,
        slideshow: updatedSlideshow,
      },
    });
    setCurrent(targetIndex);
  };

  const handleAddImage = () => {
    const updatedSlideshow = [
      ...(page?.introduction?.slideshow || []),
      { url: "", publicId: "", order: page?.introduction?.slideshow?.length || 0 },
    ];

    setPage({
      ...page,
      introduction: {
        ...page.introduction,
        slideshow: updatedSlideshow,
      },
    });
    setCurrent(updatedSlideshow.length - 1);
    window.requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const handleRemoveCurrentImage = () => {
    const slideshow = page?.introduction?.slideshow || [];
    if (!slideshow.length) return;

    const updatedSlideshow = slideshow.filter((_, index) => index !== current);
    setPage({
      ...page,
      introduction: {
        ...page.introduction,
        slideshow: updatedSlideshow,
      },
    });
    setCurrent((prev) => Math.min(prev, Math.max(updatedSlideshow.length - 1, 0)));
  };

  const handleCounterpartItemChange = (index, value) => {
    const items = Array.from(
      { length: Math.max(COUNTERPART_SLOTS, page?.counterparts?.items?.length || 0) },
      (_, itemIndex) => ({
        text: page?.counterparts?.items?.[itemIndex]?.text || "",
        order: itemIndex,
      }),
    );

    items[index] = { ...items[index], text: value };
    setPage({
      ...page,
      counterparts: { ...page.counterparts, items },
    });
  };

  const counterpartItems = page?.counterparts?.items || [];

  return (

    <>
    {adminPage ? (
      <header className="admin-para-empresas__header">
        <h1>EDITAR SEÇÃO</h1>
        <p>Para Empresas</p>
      </header>
    ) : (
      <PageHero
        imageUrl={heroFallbackUrl}
        imageShape="provided"
        title="Para Empresas"
      />
    )}
    {!adminPage && <ApoieButton />}
    <main className="para-empresas-page">
      <Editable
        active={(editMode && adminPage)}
        view={<p>{page?.introduction.text}</p>}
        edit={
          <textarea
            className="para-empresas-page-p__textarea"
            value={page?.introduction.text ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                introduction: {
                  ...page.introduction,
                  text: e.target.value,
                },
              })
            }
          />
        }
      />
      {(page?.introduction.slideshow.length !== 0 || (editMode && adminPage)) && (
        <section className="para-empresas-slideshow-section">
          {page?.introduction.slideshow.length !== 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <button
                className="para-empresas-slideshow-arrow left"
                onClick={anterior}
              >
                <span></span>
              </button>

              <div className="para-empresas-slideshow-viewport">

                <div
                  className="para-empresas-slideshow-track"
                  style={{
                    transform: `translateX(-${current * 1032}px)`,
                    "--slide-index": current,
                  }}
                >
                  {page?.introduction.slideshow.map((slide, index) => (
                    <div
                      key={slide._id || index}
                      className={`para-empresas-slideshow-card ${current === index ? "active" : "inactive"
                        }`}
                    >
                      {slide.url ? (
                        <img
                          src={slide.url}
                          alt="Slide em destaque"
                          className="para-empresas-slideshow__image"
                        />
                      ) : (
                        <div className="para-empresas-slideshow-empty">
                          Selecione uma imagem
                        </div>
                      )}

                    </div>
                  ))}

                </div>

              </div>

              <button
                className="para-empresas-slideshow-arrow right"
                onClick={proximo}
              >
                <span></span>
              </button>
            </div>
          ) : (
            <p className="para-empresas-slideshow-empty">Nenhuma imagem cadastrada.</p>
          )}
          {editMode && adminPage && (
            <div className="upload-image-container">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <button
                className="upload-image-btn"
                onClick={() => fileInputRef.current.click()}
                title="Substituir imagem atual"
              >
                <UploadImageIcon />
              </button>
              <button
                type="button"
                className="para-empresas-gallery-btn"
                onClick={handleAddImage}
              >
                + Adicionar imagem
              </button>
              {page?.introduction.slideshow.length > 0 && (
                <button
                  type="button"
                  className="para-empresas-gallery-btn para-empresas-gallery-btn--danger"
                  onClick={handleRemoveCurrentImage}
                >
                  Remover imagem atual
                </button>
              )}
            </div>
          )}
        </section>
      )}
      <Editable
        active={(editMode && adminPage)}
        view={<h2>{page?.counterparts.title}</h2>}
        edit={
          <input
            className="para-empresas-page-h2__input"
            value={page?.counterparts.title ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                counterparts: {
                  ...page.counterparts,
                  title: e.target.value,
                },
              })
            }
          />
        }
      />
      <Editable
        active={(editMode && adminPage)}
        view={<p>{page?.counterparts.text}</p>}
        edit={
          <textarea
            className="para-empresas-page-p__textarea"
            value={page?.counterparts.text ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                counterparts: {
                  ...page.counterparts,
                  text: e.target.value,
                },
              })
            }
          />
        }
      />
      <div className="para-empresas-counterparts__items">
        {Array.from(
          {
            length: Math.max(COUNTERPART_SLOTS, counterpartItems.length),
          },
          (_, index) => {
            const item = counterpartItems[index];

            return editMode && adminPage ? (
              <input
                key={item?._id || index}
                type="text"
                value={item?.text || ""}
                placeholder={`Contrapartida ${index + 1}`}
                onChange={(event) =>
                  handleCounterpartItemChange(index, event.target.value)
                }
              />
            ) : (
              <span key={item?._id || index}>{item?.text || ""}</span>
            );
          },
        )}
      </div>
      <section className="para-empresas-examples">
        <Editable
          active={(editMode && adminPage)}
          view={<h2>{page?.examples.title}</h2>}
          edit={
            <input
              className="para-empresas-page-h2__input"
              value={page?.examples.title ?? ""}
              onChange={(e) =>
                setPage({
                  ...page,
                  examples: {
                    ...page.examples,
                    title: e.target.value,
                  },
                })
              }
            />
          }
        />
        <div className="para-empresas-video-card">
          {isLoading ? (
            <div className="para-empresas-video-card__message" role="status">
              Carregando video
            </div>
          ) : videoEmbed ? (
            <div className="para-empresas-video-frame">
              <iframe
                className="para-empresas-video-frame__iframe"
                src={videoEmbed.src}
                title={videoEmbed.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="para-empresas-video-card__message">
              {VIDEO_UNAVAILABLE_MESSAGE}
            </div>
          )}
        </div>
        {editMode && adminPage &&
          <input
            className="para-empresas-video-url__input"
            type="url"
            value={page?.examples.video.url ?? ""}
            onChange={(e) => {
              const updatedVideo = {
                ...page.examples.video,
                url: e.target.value
              };
              setPage({
                ...page,
                examples: {
                  ...page.examples,
                  video: updatedVideo
                }
              });
              setVideoEmbed(getInstitutionalVideoEmbed(updatedVideo));
            }}
            placeholder="Link do video do YouTube ou Instagram"
          />}
      </section>
      <Editable
        active={(editMode && adminPage)}
        view={<h2>{page?.incomeTax.title}</h2>}
        edit={
          <input
            className="para-empresas-page-h2__input"
            value={page?.incomeTax.title ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                incomeTax: {
                  ...page.incomeTax,
                  title: e.target.value,
                },
              })
            }
          />
        }
      />
      <Editable
        active={(editMode && adminPage)}
        view={<p>{page?.incomeTax.text}</p>}
        edit={
          <textarea
            className="para-empresas-page-p__textarea"
            value={page?.incomeTax.text ?? ""}
            onChange={(e) =>
              setPage({
                ...page,
                incomeTax: {
                  ...page.incomeTax,
                  text: e.target.value,
                },
              })
            }
          />
        }
      />
      <Editable
        active={(editMode && adminPage)}
        view={
          <div className="para-empresas__meeting-link">
            <a
              href={page?.incomeTax.meetingLink || undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              Clique aqui e agende uma reunião!
            </a>
          </div>
        }
        edit={
          <div className="para-empresas__meeting-link">
            <h2>Edite o link de agendamento de reunião</h2>
            <input
              className="para-empresas-page-h2__input"
              type="text"
              value={page?.incomeTax.meetingLink ?? ""}
              onChange={(e) =>
                setPage({
                  ...page,
                  incomeTax: {
                    ...page.incomeTax,
                    meetingLink: e.target.value,
                  },
                })
              }
            />
          </div>
        }
      />
      {adminPage && (<div className="para-empresas-page-admin">
        {error && <span className="error-message">{error.response?.data?.error || error.error ||
          "Ocorreu um erro, verifique os campos"}</span>}
        <FormActions
          editing={editMode}
          onEdit={handleEditClick}
          onCancel={handleEditClick}
          onSave={handleSave}
        />
      </div>)}
    </main>
    </>

  );
};

export default ParaEmpresas;
